import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Table,
  Typography,
  Space,
  Tooltip,
  Modal,
  DatePicker
} from 'antd';
import {
  EditFilled,
  DeleteFilled,
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  EyeFilled,
  CheckCircleFilled,
  CloseCircleFilled
} from '@ant-design/icons';
import BatteryGauge from 'react-battery-gauge';
import {
  apiDeleteConfig,
  apiDeleteReplacement,
  apiGetAllConfigs,
  apiGetAllReplacements,
  apiGetDevicesInformation
} from '@services/ManageDevices/DeviceServices';
import { showError, showWarning } from '@components/notification';
import { communityList, convertLocaltimeToUTC, downloadBlobFile } from '@utils/utils';
import StatCards from './StatCards';
import { useNavigate } from 'react-router-dom';
import ModalImportFile from './ModalImportFile';
import { UploadFileOutlined } from '@mui/icons-material';
import { URL_API } from '@configs/index';
import moment from 'moment';
import { apiDeleteCustomer, apiGetAllCustomer } from '@services/ManageCustomers/CustomerServices';
import { usePermission } from '@contexts/Auth/UsePermission';

const { Option } = Select;
const { Title, Paragraph } = Typography;

const { RangePicker } = DatePicker;

export default function List() {
  const [selectedCommunity, setSelectedCommunity] = useState('ALL');
  const [deviceList, setDeviceList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();
  const [visibleModalImport, setVisibleModalImport] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [period, setPeriod] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const { config, isSuperAdmin } = usePermission();
  const fetchData = async () => {
    try {
      setIsFetching(true);
      const res = await apiGetAllConfigs({
        page: currentPage,
        size: pageSize,
        keyWord: search?.trim() ?? undefined,
        serialNo: serialNo?.trim() ?? undefined,
        period: selectedMonth ?? undefined
      });
      setDeviceList(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      showError('Something went wrong!');
      console.log(err);
    } finally {
      setIsFetching(false);
    }
  };

  const handleEdit = (record) => {
    navigate(`/config/update/${record.id}`);
  };

  const handleViewDetail = (record) => {
    navigate(`/config/detail/${record.id}`);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xoá cấu hình ghi số này?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        await apiDeleteConfig(record?.id);
        fetchData();
      },
      onCancel() {
        console.log('Cancel deletion');
      }
    });
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, selectedCommunity]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 80,
        fixed: 'left'
      },
      {
        title: 'Trạng thái',
        dataIndex: 'status',
        render: (text, record) => {
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {record?.status === '1' ? (
                  <CheckCircleFilled style={{ fontSize: '20px', color: '#10b981' }} />
                ) : (
                  <CloseCircleFilled style={{ fontSize: '20px', color: '#e11d48' }} />
                )}
                <Typography.Text
                  style={{
                    fontSize: '14px',
                    color: record?.status === '1' ? '#10b981' : '#e11d48'
                  }}>
                  {record?.status === '1' ? 'Đã ghi' : 'Chưa ghi'}
                </Typography.Text>
              </div>
            </>
          );
        },
        width: 120
      },
      {
        title: 'DBĐD',
        dataIndex: 'customer_id',
        fixed: 'left',
        width: 100
      },
      {
        title: 'Tên KH',
        dataIndex: 'customer_name',
        fixed: 'left',
        width: 150
      },
      {
        title: 'Tháng cước',
        dataIndex: 'period',
        render: (text, record) => {
          return <>{moment(record?.period)?.format('MM/YYYY')}</>;
        },
        width: 100
      },
      {
        title: 'Ngày bắt đầu ghi số',
        dataIndex: 'start_record_date',
        render: (text, record) => {
          return <>{moment(record?.start_record_date)?.format('DD/MM/YYYY')}</>;
        },
        width: 150
      },
      {
        title: 'Ngày đọc chỉ số',
        dataIndex: 'end_record_date',
        render: (text, record) => {
          return <>{moment(record?.end_record_date)?.format('DD/MM/YYYY')}</>;
        },
        width: 150
      },

      {
        title: 'Người tạo',
        dataIndex: 'created_by',
        render: (_, record) => {
          return (
            <>
              <div>{record?.created_by}</div>
              <div style={{ fontSize: '12px', fontStyle: 'italic', color: '#8c8c8c' }}>
                {moment(record?.created_time)?.format('DD/MM/YYYY HH:mm:ss')}
              </div>
            </>
          );
        },
        width: 100
      },
      ...(isSuperAdmin
        ? [
            {
              title: 'Người cập nhật',
              dataIndex: 'updated_by',
              render: (_, record) => {
                return (
                  <>
                    {record?.updated_by
                      ? [
                          <div key="updated-by">{record?.updated_by}</div>,
                          <div
                            key="updated-at"
                            style={{
                              fontSize: '12px',
                              fontStyle: 'italic',
                              color: '#8c8c8c'
                            }}>
                            {moment(record?.updated_time)?.format('DD/MM/YYYY HH:mm:ss')}
                          </div>
                        ]
                      : '-'}
                  </>
                );
              },
              width: 100
            }
          ]
        : []),
      {
        title: 'Hành động',
        dataIndex: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space size="middle">
            <Tooltip title="Xem chi tiết">
              <Button
                type="text"
                shape="circle"
                icon={<EyeFilled style={{ color: '#0284c7', fontSize: '18px' }} />}
                onClick={() => handleViewDetail(record)}
              />
            </Tooltip>
            {config.edit && (
              <Tooltip title="Chỉnh sửa">
                <Button
                  type="text"
                  shape="circle"
                  icon={<EditFilled style={{ color: '#0284c7', fontSize: '18px' }} />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
            )}
            {config.delete && (
              <Tooltip title="Xoá">
                <Button
                  type="danger"
                  shape="circle"
                  icon={<DeleteFilled style={{ color: '#0284c7', fontSize: '18px' }} />}
                  onClick={() => handleDelete(record)}
                />
              </Tooltip>
            )}
          </Space>
        )
      }
    ],
    [config.delete, config.edit, isSuperAdmin]
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSerialNoChange = (e) => {
    setSerialNo(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };
  const generateYearOptions = () => {
    const years = [];
    const currentYear = moment().year();
    const currentMonth = moment().month() + 1; // moment().month() returns 0-11
    const startYear = currentMonth === 12 ? currentYear + 1 : currentYear;
    for (let y = startYear; y >= currentYear - 1; y--) {
      years.push({ label: `Năm ${y}`, value: y });
    }
    return years;
  };
  const generateMonthOptions = () => {
    const months = [];
    const current = moment();
    const isCurrentYear = selectedYear === current.year();
    const isFutureYear = selectedYear > current.year();

    // If future year, only show months 1-12
    if (isFutureYear) {
      for (let m = 11; m >= 0; m--) {
        const monthMoment = moment(`${selectedYear}-01-01`).add(m, 'months');
        months.push({
          label: `Tháng cước ${monthMoment.format('MM/YYYY')}`,
          value: monthMoment.format('YYYY-MM-DD')
        });
      }
    } else {
      // Current year or past year
      const startOfYear = moment(`${selectedYear}-01-01`);
      const endMonth = isCurrentYear ? current.month() : 11;

      for (let m = endMonth; m >= 0; m--) {
        const monthMoment = startOfYear.clone().add(m, 'months');
        months.push({
          label: `Tháng cước ${monthMoment.format('MM/YYYY')}`,
          value: monthMoment.format('YYYY-MM-DD')
        });
      }
    }
    return months;
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    setSelectedMonth(null); // Reset month selection when year changes
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
  };

  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    try {
      setLoading(true);
      await downloadBlobFile(
        `${URL_API}/api/configs/export-report`,
        `Danh sách cấu hình chốt số kỳ ${moment(selectedMonth).format('MM-YYYY')}.xlsx`,
        {
          keyWord: search?.trim() ?? undefined,
          period: selectedMonth ?? undefined
        },
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <ModalImportFile
        visible={visibleModalImport}
        onCancel={() => {
          setVisibleModalImport(false);
          fetchData();
        }}
      />
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4}>Quản lý lịch ghi chỉ số</Typography.Title>
        </Col>
        <Col>
          <Space>
            {/* <Button
              icon={<PlusOutlined />}
              onClick={handleAddDevice}
              disabled
              style={{
                color: '#0284c7',
                height: '36px',
                backgroundColor: '#eef1f6',
                borderColor: '#eef1f6',
                fontSize: '18px'
              }}>
              Thêm mới
            </Button> */}
            {config.export && (
              <Button
                icon={<UploadOutlined />}
                onClick={handleExportExcelButtonClick}
                loading={loading}
                style={{
                  color: '#fff',
                  height: '36px',
                  backgroundColor: '#0284c7',
                  borderColor: '#0284c7',
                  fontSize: '18px'
                }}>
                Xuất file excel
              </Button>
            )}
            {config.import && (
              <Button
                icon={<DownloadOutlined />}
                onClick={() => {
                  setVisibleModalImport(true);
                }}
                style={{
                  color: '#fff',
                  height: '36px',
                  backgroundColor: '#0284c7',
                  borderColor: '#0284c7',
                  fontSize: '18px'
                }}>
                Nhập file excel
              </Button>
            )}
          </Space>
        </Col>
      </Row>
      <Card bodyStyle={{ padding: '8px' }}>
        <Row gutter={[8, 8]}>
          {/* <Col md={6} xs={12}>
            <Select
              value={selectedCommunity}
              onChange={setSelectedCommunity}
              style={{ width: '100%', height: '36px' }}>
              {communityList.map((community) => (
                <Option key={community.value} value={community.value}>
                  {community.label}
                </Option>
              ))}
            </Select>
          </Col> */}
          <Col md={3} xs={6}>
            <Select
              style={{ width: '100%', height: '36px' }}
              placeholder="Chọn năm"
              value={selectedYear}
              onChange={handleYearChange}>
              {generateYearOptions().map((year) => (
                <Option key={year.value} value={year.value}>
                  {year.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col md={5} xs={6}>
            <Select
              style={{ width: '100%', height: '36px' }}
              placeholder="Chọn tháng cước"
              value={selectedMonth}
              onChange={handleMonthChange}>
              {generateMonthOptions().map((month) => (
                <Option key={month.value} value={month.value}>
                  {month.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col md={5} xs={6}>
            <Input
              value={search}
              onChange={handleSearchChange}
              onPressEnter={handleSearch}
              placeholder="Tìm kiếm tên, mã kh..."
              style={{ width: '100%', height: '36px' }}
            />
          </Col>

          <Col md={5} sm={6}>
            <Button
              onClick={handleSearch}
              icon={<SearchOutlined />}
              style={{
                backgroundColor: '#0284c7',
                borderColor: '#0284c7',
                color: '#fff',
                height: '36px'
              }}>
              Tìm kiếm
            </Button>
          </Col>
        </Row>
      </Card>
      <Table
        bordered
        // size="middle"
        loading={isFetching}
        dataSource={deviceList || []}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          showSizeChanger: true,
          onChange: handleChangePage,
          onShowSizeChange: handleChangePageSize,
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
        }}
        style={{
          borderRadius: 8,
          thead: { backgroundColor: '#e2e8f0', color: '#64748b' },
          fontSize: '30px'
        }}
        scroll={{ x: 'max-content', y: 600 }}
      />
    </div>
  );
}
